import {
  type Breakpoint,
  screenSizes,
} from "@repo/fika-design-system/breakpoints"
import { createBreakpoints } from "@solid-primitives/media"
import { entries, fromEntries } from "remeda"
import type { Accessor, JSX } from "solid-js"
import { createMemo, For } from "solid-js"
import { columnClass, masonryContainerClass } from "./masonry.css"

type ColumnConfig = Partial<Record<Breakpoint, number>>
export interface MasonryProps<T> {
  items: Accessor<T[]>
  renderItem: (item: T) => JSX.Element
  columns?: ColumnConfig
}

export function Masonry<T>(props: MasonryProps<T>) {
  const columnsConfig = createMemo(() => props.columns ?? { l: 2 })
  const matches = createBreakpoints(
    fromEntries(
      entries(screenSizes).map(([key, value]) => [key, `${value}px`]),
    ),
  )
  const columnCount = createMemo(() => {
    const config = columnsConfig()
    const key = matches.key
    if (config[key]) return config[key]

    const breakpointOrder: Breakpoint[] = ["xxl", "xl", "l", "m", "s", "xs"]
    for (const bp of breakpointOrder) {
      if (matches[bp] && config[bp]) {
        return config[bp]
      }
    }

    return 1
  })

  const columns = createMemo(() => {
    const count = columnCount()
    const result: T[][] = Array.from({ length: count }, () => [])

    props.items().forEach((item, i) => {
      result[i % count]!.push(item)
    })

    return result
  })

  return (
    <div class={masonryContainerClass}>
      <For each={columns()}>
        {column => (
          <div class={columnClass}>
            <For each={column}>{props.renderItem}</For>
          </div>
        )}
      </For>
    </div>
  )
}
